import { useMemo } from 'react';

/**
 * Extracts filter-only values from a Form currentValues snapshot.
 * Excludes non-filter fields such as the "color" styling field.
 *
 * @param {object} currentValues  - Full Form values ({ [filterId]: { min, max } } plus non-filter fields)
 * @param {Array}  filterConfigs  - Config array to derive which keys are filter fields
 * @returns {object} - { [filterId]: { min, max } }
 */
const extractFilterValues = (currentValues, filterConfigs) => {
	if (!currentValues || !filterConfigs?.length) return {};
	const result = {};
	for (const config of filterConfigs) {
		const v = currentValues[config.id];
		if (v) result[config.id] = v;
	}
	return result;
};

/**
 * Resolves the *effective* collection- and object-level filter values from the
 * live form state of both filter panels. Pure computation, no store interaction —
 * shared by useFilteredFeatures (3857 features, written to the store) and
 * CollectionSingleImageLayer (normalized features for the open image, filtered
 * on demand), so both apply identical filter semantics without duplicating this
 * resolution logic.
 *
 * @param {object}      collectionCurrentValues      - collection form draft values
 * @param {Array}       collectionFilterConfigs      - collection filter configs
 * @param {string|null} selectedObjectKey            - object whose panel is open
 * @param {boolean}     objectFiltersDirty           - selected object has unsaved filter edits (not color)
 * @param {object}      objectCurrentValues          - selected object form draft values
 * @param {Array}       objectEffectiveFilterConfigs - selected object filter configs
 * @param {Array}       objects                      - collection objects (for saved per-object filters)
 * @returns {{ collectionFilterValues: object, objectFilterValues: object }}
 */
const useCollectionFilterValues = (
	collectionCurrentValues,
	collectionFilterConfigs,
	selectedObjectKey,
	objectFiltersDirty,
	objectCurrentValues,
	objectEffectiveFilterConfigs,
	objects
) => {
	const collectionFilterValues = useMemo(
		() => extractFilterValues(collectionCurrentValues, collectionFilterConfigs),
		[collectionCurrentValues, collectionFilterConfigs]
	);

	// Saved per-object filters, applied for every object regardless of which
	// panel is open — this is what makes a saved filter a persistent setting.
	const savedObjectFilters = useMemo(() => {
		const map = {};
		for (const obj of objects ?? []) {
			if (obj.savedFilters && Object.keys(obj.savedFilters).length) {
				map[obj.key] = obj.savedFilters;
			}
		}
		return map;
	}, [objects]);

	// Overlay the live draft for the object being actively edited so its unsaved
	// filter changes preview immediately.
	const objectFilterValues = useMemo(() => {
		if (!selectedObjectKey || !objectFiltersDirty) return savedObjectFilters;
		const live = extractFilterValues(objectCurrentValues, objectEffectiveFilterConfigs);
		return { ...savedObjectFilters, [selectedObjectKey]: live };
	}, [
		savedObjectFilters,
		selectedObjectKey,
		objectFiltersDirty,
		objectCurrentValues,
		objectEffectiveFilterConfigs,
	]);

	// Wrapped in its own memo so consumers depending on the combined result as a
	// single value (e.g. an effect/memo dependency) don't recompute on every
	// render when neither piece actually changed.
	return useMemo(
		() => ({ collectionFilterValues, objectFilterValues }),
		[collectionFilterValues, objectFilterValues]
	);
};

export default useCollectionFilterValues;
